import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { articleGenerationService } from '../../services/articleGenerationService';
import { CriticRoundResult, RevisionRow } from '../../types/articleGeneration';
import MaterialIcon from '../shared/MaterialIcon';

interface Props {
  generationId: string;
  critique?: Record<string, { rounds: number; outcome: string; reason?: string }> | null;
}

type PlanReviewState = {
  outcome?: string;
  reviewer?: string;
  needsFix?: boolean;
  fixed?: boolean;
  review?: {
    h1?: { ok: boolean; suggestion: string };
    headings?: { index: number; ok: boolean; suggestion: string }[];
    briefs?: { index: number; ok: boolean; suggestion: string }[];
    structure_notes?: string;
  };
};

type LanguagePolishState = {
  outcome: string; // polished | skipped | in_progress
  changed?: number;
  rejected?: number;
  reason?: string;
};

/** The language polish pass: how many passages were reworded, how many kept. */
const LanguagePolishCard: React.FC<{ target: 'plan' | 'article'; state: LanguagePolishState }> = ({
  target,
  state,
}) => {
  const { t } = useTranslation();
  return (
    <div data-testid={`language-polish-${target}`} className="border rounded-lg p-3 bg-white text-xs">
      <span className="font-semibold">
        {t(target === 'plan' ? 'article.language_polish_plan' : 'article.language_polish_article')}
      </span>{' '}
      {state.outcome === 'skipped'
        ? t('article.language_polish_skipped')
        : t('article.language_polish_summary', {
            changed: state.changed ?? 0,
            rejected: state.rejected ?? 0,
          })}
    </div>
  );
};

/** The plan reviewer's verdict (headings / briefs / the light check before writing). */
const PlanReviewCard: React.FC<{ plan: PlanReviewState }> = ({ plan }) => {
  const { t } = useTranslation();
  const r = plan.review ?? {};
  const lines: string[] = [];
  if (r.h1 && !r.h1.ok) lines.push(`H1: ${r.h1.suggestion}`);
  for (const h of r.headings ?? []) if (!h.ok) lines.push(`${t('article.dim_section', { index: h.index })}: ${h.suggestion}`);
  for (const b of r.briefs ?? []) if (!b.ok) lines.push(`${t('article.brief_label')} ${b.index}: ${b.suggestion}`);
  return (
    <div data-testid="plan-review-card" className="border rounded-lg p-3 bg-white">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h4 className="font-semibold text-sm">{t('article.critique_target_plan')}</h4>
        {plan.reviewer && <span className="font-mono text-xs text-gray-400">{plan.reviewer}</span>}
        <span
          className={`text-xs px-2 py-0.5 rounded-full border ${
            plan.needsFix ? 'text-amber-800 bg-amber-50 border-amber-300' : 'text-green-700 bg-green-50 border-green-200'
          }`}
        >
          {t(plan.needsFix ? 'article.plan_review_fixed' : 'article.plan_review_ok')}
        </span>
      </div>
      {lines.length > 0 && (
        <ul className="text-xs text-gray-700 space-y-1 list-disc pl-5">
          {lines.map((l, i) => (
            <li key={i}>{l}</li>
          ))}
        </ul>
      )}
      {r.structure_notes && (
        <div className="mt-2 text-xs text-gray-600">
          <span className="font-semibold">{t('article.plan_review_notes')}:</span> {r.structure_notes}
        </div>
      )}
    </div>
  );
};

type DimensionValue = { ok?: boolean; feedback?: string };
type SectionDimension = { index: number; ok?: boolean; feedback?: string };

const DIMENSION_ORDER = ['headings', 'logic', 'flow', 'human_feel', 'added_value'] as const;

const outcomeTone: Record<string, string> = {
  pass: 'text-green-700 bg-green-50 border-green-200',
  unresolved: 'text-amber-800 bg-amber-50 border-amber-300',
  skipped: 'text-gray-600 bg-gray-50 border-gray-200',
};

const criticSections = (c: CriticRoundResult): SectionDimension[] => {
  const secs = (c.dimensions as { sections?: unknown } | undefined)?.sections;
  return Array.isArray(secs) ? (secs as SectionDimension[]) : [];
};

const CriticDimensions: React.FC<{ dimensions: Record<string, unknown> }> = ({ dimensions }) => {
  const { t } = useTranslation();
  const row = (label: string, dim: DimensionValue, key: string) => (
    <div key={key} className="flex gap-2 text-xs">
      <MaterialIcon
        name={dim.ok ? 'check_circle' : 'cancel'}
        className={`text-sm ${dim.ok ? 'text-green-600' : 'text-red-600'}`}
      />
      <span className="font-medium text-gray-700 shrink-0">{label}:</span>
      <span className="text-gray-600">{dim.feedback || '—'}</span>
    </div>
  );
  return (
    <div className="mt-1 space-y-1 pl-4">
      {DIMENSION_ORDER.filter((k) => dimensions[k]).map((k) =>
        row(t(`article.dim_${k}`), dimensions[k] as DimensionValue, k)
      )}
      {Array.isArray(dimensions.sections) &&
        (dimensions.sections as SectionDimension[]).map((s) =>
          row(
            s.index === 0
              ? t('article.comment_lead')
              : t('article.dim_section', { index: s.index }),
            { ok: s.ok, feedback: s.feedback },
            `section-${s.index}`
          )
        )}
    </div>
  );
};

/** How each section's quality (critics marking it ok) moved across rounds. */
const SectionQualityMatrix: React.FC<{ rows: RevisionRow[] }> = ({ rows }) => {
  const { t } = useTranslation();
  const { roundNums, sections, data } = useMemo(() => {
    const sectionSet = new Set<number>();
    const d: Record<number, Record<number, { pos: number; assessed: number }>> = {};
    for (const rev of rows) {
      for (const c of (rev.critique ?? []) as CriticRoundResult[]) {
        for (const s of criticSections(c)) {
          sectionSet.add(s.index);
          (d[s.index] ??= {})[rev.round] ??= { pos: 0, assessed: 0 };
          d[s.index][rev.round].assessed++;
          if (s.ok) d[s.index][rev.round].pos++;
        }
      }
    }
    return {
      roundNums: [...new Set(rows.map((r) => r.round))].sort((a, b) => a - b),
      sections: [...sectionSet].sort((a, b) => a - b),
      data: d,
    };
  }, [rows]);

  if (sections.length === 0) return null;

  const cellTone = (pos: number, assessed: number) =>
    assessed === 0
      ? 'text-gray-300'
      : pos === assessed
        ? 'text-green-700 bg-green-50'
        : pos === 0
          ? 'text-red-700 bg-red-50'
          : 'text-amber-800 bg-amber-50';

  return (
    <div className="mb-4 overflow-x-auto">
      <div className="text-xs font-semibold text-gray-600 mb-1">
        {t('article.section_quality_title')}
      </div>
      <table className="text-xs border rounded-md">
        <thead>
          <tr className="text-gray-500">
            <th className="p-2 text-left font-medium">{t('article.section_quality_section')}</th>
            {roundNums.map((r) => (
              <th key={r} className="p-2 font-medium whitespace-nowrap">
                {t('article.critique_round_label', { round: r })}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sections.map((i) => (
            <tr key={i} className="border-t">
              <td className="p-2 whitespace-nowrap">
                {i === 0 ? t('article.comment_lead') : t('article.dim_section', { index: i })}
              </td>
              {roundNums.map((r) => {
                const cell = data[i]?.[r];
                return (
                  <td
                    key={r}
                    className={`p-2 text-center ${cellTone(cell?.pos ?? 0, cell?.assessed ?? 0)}`}
                  >
                    {cell ? `${cell.pos}/${cell.assessed}` : '—'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

type Mark = 'ok' | 'not ok' | 'failed' | 'not rated' | 'positive' | 'negative';
const MARKS: Record<Mark, { icon: string; className: string }> = {
  ok: { icon: 'check_circle', className: 'text-green-600' },
  'not ok': { icon: 'cancel', className: 'text-red-600' },
  failed: { icon: 'error', className: 'text-gray-400' },
  'not rated': { icon: 'remove', className: 'text-gray-300' },
  positive: { icon: 'thumb_up', className: 'text-green-600' },
  negative: { icon: 'thumb_down', className: 'text-amber-600' },
};
const MarkIcon: React.FC<{ mark: Mark }> = ({ mark }) => (
  <span role="img" aria-label={mark} className="inline-flex">
    <MaterialIcon name={MARKS[mark].icon} className={`text-base ${MARKS[mark].className}`} />
  </span>
);

/** One card per critic model: its verdict and per-section ok, round by round. */
const CriticsView: React.FC<{ rows: RevisionRow[] }> = ({ rows }) => {
  const { t } = useTranslation();
  const [openSlots, setOpenSlots] = useState<Set<number>>(new Set());
  const { roundNums, critics, sections } = useMemo(() => {
    const roundNums = [...new Set(rows.map((r) => r.round))].sort((a, b) => a - b);
    const critics = new Map<number, { label: string; model: string }>();
    const sectionSet = new Set<number>();
    for (const rev of rows) {
      for (const c of (rev.critique ?? []) as CriticRoundResult[]) {
        critics.set(c.slot, { label: c.label, model: c.modelId });
        for (const s of criticSections(c)) sectionSet.add(s.index);
      }
    }
    return {
      roundNums,
      critics: [...critics.entries()].sort((a, b) => a[0] - b[0]),
      sections: [...sectionSet].sort((a, b) => a - b),
    };
  }, [rows]);

  const outcomeAt = (slot: number, round: number): CriticRoundResult | undefined =>
    ((rows.find((r) => r.round === round)?.critique ?? []) as CriticRoundResult[]).find(
      (c) => c.slot === slot
    );
  const verdictMark = (c: CriticRoundResult | undefined): Mark =>
    !c ? 'not rated' : !c.ok ? 'failed' : c.verdict === 'positive' ? 'positive' : 'negative';
  const sectionMark = (c: CriticRoundResult | undefined, index: number): Mark => {
    if (!c) return 'not rated';
    if (!c.ok) return 'failed';
    const s = criticSections(c).find((x) => x.index === index);
    return !s ? 'not rated' : s.ok ? 'ok' : 'not ok';
  };
  const toggle = (slot: number) =>
    setOpenSlots((prev) => {
      const next = new Set(prev);
      if (next.has(slot)) next.delete(slot);
      else next.add(slot);
      return next;
    });

  return (
    <div className="space-y-4">
      {critics.map(([slot, meta]) => {
        const all = roundNums.map((r) => outcomeAt(slot, r));
        const approved = all.filter((c) => c?.ok && c.verdict === 'positive').length;
        const rejected = all.filter((c) => c?.ok && c.verdict === 'negative').length;
        const failed = all.filter((c) => c && !c.ok).length;
        const open = openSlots.has(slot);
        return (
          <div
            key={slot}
            data-testid={`critic-view-${slot}`}
            className="border rounded-lg p-3 bg-white"
          >
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="text-sm font-medium flex items-center gap-2">
                <span>{meta.label}</span>
                <span className="font-mono text-xs text-gray-400">{meta.model}</span>
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                {t('article.critique_round_counts', { approved, rejected, failed })}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="text-xs border rounded-md">
                <thead>
                  <tr className="text-gray-500">
                    <th className="p-2 text-left font-medium">
                      {t('article.section_quality_section')}
                    </th>
                    {roundNums.map((r) => (
                      <th key={r} className="p-2 font-medium whitespace-nowrap">
                        {t('article.critique_round_label', { round: r })}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t bg-gray-50">
                    <td className="p-2 whitespace-nowrap font-medium">
                      {t('article.critique_verdict_row')}
                    </td>
                    {roundNums.map((r) => (
                      <td key={r} className="p-2 text-center">
                        <MarkIcon mark={verdictMark(outcomeAt(slot, r))} />
                      </td>
                    ))}
                  </tr>
                  {sections.map((i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2 whitespace-nowrap">
                        {i === 0
                          ? t('article.comment_lead')
                          : t('article.dim_section', { index: i })}
                      </td>
                      {roundNums.map((r) => (
                        <td key={r} className="p-2 text-center">
                          <MarkIcon mark={sectionMark(outcomeAt(slot, r), i)} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              onClick={() => toggle(slot)}
              className="mt-2 text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
            >
              <MaterialIcon name={open ? 'expand_less' : 'expand_more'} className="text-sm" />
              {t(open ? 'article.critique_hide_feedback' : 'article.critique_show_feedback')}
            </button>
            {open && (
              <div className="mt-2 border-t pt-2 space-y-3">
                {roundNums.map((r) => {
                  const c = outcomeAt(slot, r);
                  return (
                    <div key={r}>
                      <div className="flex items-center gap-2 text-xs font-medium">
                        <span>{t('article.critique_round_label', { round: r })}</span>
                        <MarkIcon mark={verdictMark(c)} />
                      </div>
                      {!c ? (
                        <div className="text-xs text-gray-500 pl-4">
                          {t('article.critique_not_rated')}
                        </div>
                      ) : c.error ? (
                        <div className="text-xs text-gray-500 pl-4">
                          {t('article.critique_unavailable')}: {c.error}
                        </div>
                      ) : c.dimensions ? (
                        <CriticDimensions dimensions={c.dimensions} />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const CritiqueTimeline: React.FC<Props> = ({ generationId, critique }) => {
  const { t } = useTranslation();
  const [revisions, setRevisions] = useState<RevisionRow[]>([]);
  const [openRounds, setOpenRounds] = useState<Set<string>>(new Set());
  // 'rounds' = what happened round by round; 'critics' = how each model judged.
  const [view, setView] = useState<'rounds' | 'critics'>('rounds');

  useEffect(() => {
    articleGenerationService
      .listRevisions(generationId)
      .then(setRevisions)
      .catch(() => setRevisions([]));
  }, [generationId]);

  const rounds = useMemo(() => revisions.filter((r) => r.critique !== null), [revisions]);

  const toggleRound = (id: string) =>
    setOpenRounds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const plan = (critique as Record<string, unknown> | null | undefined)?.plan as PlanReviewState | undefined;
  const planCard = plan?.outcome === 'reviewed' ? <PlanReviewCard plan={plan} /> : null;
  const languageCards = (['plan', 'article'] as const)
    .map((target) => {
      const state = (critique as Record<string, unknown> | null | undefined)?.[`language_${target}`] as
        | LanguagePolishState
        | undefined;
      return state && state.outcome !== 'in_progress' ? (
        <LanguagePolishCard key={target} target={target} state={state} />
      ) : null;
    })
    .filter(Boolean);
  const skipReasons = Object.values(critique ?? {}).filter((c) => c.outcome === 'skipped');
  if (rounds.length === 0) {
    const note = skipReasons.length > 0
      ? t(skipReasons.some((c) => c.reason === 'critique_error') ? 'article.critique_skipped_error' : 'article.critique_skipped')
      : t('article.critique_none');
    return (
      <div className="space-y-4">
        {planCard}
        {languageCards}
        <div className="text-sm text-gray-500">{note}</div>
      </div>
    );
  }

  const byTarget: Record<string, RevisionRow[]> = {};
  for (const r of rounds) (byTarget[r.target] ??= []).push(r);

  const viewButton = (id: 'rounds' | 'critics', label: string) => (
    <button
      type="button"
      onClick={() => setView(id)}
      aria-pressed={view === id}
      className={`px-3 py-1 text-xs rounded-md border ${
        view === id
          ? 'bg-blue-600 text-white border-blue-600'
          : 'bg-white text-gray-700 hover:bg-gray-50'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6">
      {planCard}
        {languageCards}
      <div className="flex gap-1">
        {viewButton('rounds', t('article.critique_view_rounds'))}
        {viewButton('critics', t('article.critique_view_critics'))}
      </div>
      {(['skeleton', 'article'] as const)
        .filter((target) => byTarget[target]?.length)
        .map((target) => {
          const summary = critique?.[target];
          return (
            <div key={target}>
              <div className="flex items-center gap-2 mb-2">
                <h4 className="font-semibold text-sm">{t(`article.critique_target_${target}`)}</h4>
                {summary && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full border ${
                      outcomeTone[summary.outcome] ?? outcomeTone.skipped
                    }`}
                  >
                    {t('article.critique_rounds_summary', {
                      count: summary.rounds,
                      outcome: t(`article.critique_outcome_${summary.outcome}`, summary.outcome),
                    })}
                  </span>
                )}
              </div>

              {view === 'critics' ? (
                <CriticsView rows={byTarget[target]} />
              ) : (
                <>
                  <SectionQualityMatrix rows={byTarget[target]} />

                  <ol className="space-y-3">
                    {byTarget[target].map((rev) => {
                      const critics = (rev.critique ?? []) as CriticRoundResult[];
                      const approved = critics.filter(
                        (c) => c.ok && c.verdict === 'positive'
                      ).length;
                      const rejected = critics.filter(
                        (c) => c.ok && c.verdict === 'negative'
                      ).length;
                      const failed = critics.filter((c) => !c.ok).length;
                      const open = openRounds.has(rev.id);
                      return (
                        <li key={rev.id} className="border rounded-lg p-3 bg-white">
                          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                            <div className="text-sm font-medium">
                              {t('article.critique_round_label', { round: rev.round })}
                              <span className="ml-2 text-xs font-normal text-gray-400">
                                {new Date(rev.created_at).toLocaleString()}
                              </span>
                            </div>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                              {t('article.critique_round_counts', {
                                approved,
                                rejected,
                                failed,
                              })}
                            </span>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {critics.map((c) => {
                              const tone = !c.ok
                                ? 'bg-gray-100 text-gray-500 border-gray-200'
                                : c.verdict === 'positive'
                                  ? 'bg-green-50 text-green-700 border-green-200'
                                  : 'bg-amber-50 text-amber-800 border-amber-300';
                              return (
                                <span
                                  key={`${rev.id}-${c.slot}`}
                                  className={`text-xs px-2 py-1 rounded-md border inline-flex items-center gap-1 ${tone}`}
                                >
                                  <MaterialIcon
                                    name={
                                      c.ok
                                        ? c.verdict === 'positive'
                                          ? 'thumb_up'
                                          : 'thumb_down'
                                        : 'error'
                                    }
                                    className="text-sm"
                                  />
                                  {c.label}
                                </span>
                              );
                            })}
                          </div>

                          <button
                            type="button"
                            onClick={() => toggleRound(rev.id)}
                            className="mt-2 text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                          >
                            <MaterialIcon
                              name={open ? 'expand_less' : 'expand_more'}
                              className="text-sm"
                            />
                            {t(
                              open
                                ? 'article.critique_hide_feedback'
                                : 'article.critique_show_feedback'
                            )}
                          </button>

                          {open && (
                            <div className="mt-2 border-t pt-2 space-y-3">
                              {critics.map((c) => (
                                <div key={`${rev.id}-${c.slot}-detail`}>
                                  <div className="flex items-center gap-1 text-xs font-medium">
                                    <MaterialIcon
                                      name={
                                        c.ok
                                          ? c.verdict === 'positive'
                                            ? 'thumb_up'
                                            : 'thumb_down'
                                          : 'error'
                                      }
                                      className={`text-sm ${
                                        !c.ok
                                          ? 'text-red-500'
                                          : c.verdict === 'positive'
                                            ? 'text-green-600'
                                            : 'text-amber-600'
                                      }`}
                                    />
                                    {c.label}
                                  </div>
                                  {c.error ? (
                                    <div className="text-xs text-gray-500 pl-4">
                                      {t('article.critique_unavailable')}: {c.error}
                                    </div>
                                  ) : c.dimensions ? (
                                    <CriticDimensions dimensions={c.dimensions} />
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                </>
              )}
            </div>
          );
        })}
    </div>
  );
};

export default CritiqueTimeline;
